import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import AdminSidebar from 'admin/sidebar/AdminSidebar';
import { MdDeleteOutline } from 'react-icons/md';
import './SuperAdminPackages.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend.washta.com';
const TEMPLATES_URL = `${API_BASE}/api/package-sets/admin/tier-templates`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessTokenAdmin')}`,
});

const isValidWeeklyPattern = (washCount, daysPerWeek) => {
  if (!washCount || !daysPerWeek) return false;
  const weeksPerMonth = washCount / daysPerWeek;
  return weeksPerMonth >= 3 && weeksPerMonth <= 5 && Number.isInteger(weeksPerMonth);
};

const WashCounts = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ washCount: '', daysPerWeek: '', defaultDisplayName: '', sortOrder: '' });
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(TEMPLATES_URL, { headers: authHeader() });
      setTemplates(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load wash count templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { window.scrollTo(0, 0); fetchTemplates(); }, []);

  const usageCount = useMemo(() => ({}), [templates]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const washCount = Number(form.washCount);
    const daysPerWeek = Number(form.daysPerWeek);
    if (!washCount || !daysPerWeek || !form.defaultDisplayName.trim()) {
      toast.error('Wash count, days/week and display name are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        TEMPLATES_URL,
        {
          washCount,
          daysPerWeek,
          defaultDisplayName: form.defaultDisplayName.trim(),
          sortOrder: form.sortOrder ? Number(form.sortOrder) : undefined,
        },
        { headers: authHeader() }
      );
      setTemplates((prev) => [...prev, res.data.data].sort((a, b) => a.sortOrder - b.sortOrder));
      setForm({ washCount: '', daysPerWeek: '', defaultDisplayName: '', sortOrder: '' });
      setShowAddModal(false);
      toast.success('Template added');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to add template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (tpl) => {
    try {
      const res = await axios.patch(`${TEMPLATES_URL}/${tpl._id}/toggle`, {}, { headers: authHeader() });
      setTemplates((prev) => prev.map((t) => (t._id === tpl._id ? res.data.data : t)));
      toast.success(res.data.data.isActive ? 'Template activated' : 'Template deactivated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to toggle template');
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.defaultDisplayName}" (${tpl.washCount} washes)?`)) return;
    try {
      await axios.delete(`${TEMPLATES_URL}/${tpl._id}`, { headers: authHeader() });
      setTemplates((prev) => prev.filter((t) => t._id !== tpl._id));
      toast.success('Template deleted');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to delete template');
    }
  };

  return (
    <div className="mainDiv-all-sideBar">
      <div className="div-sidebar"><AdminSidebar /></div>
      <div className="div-Dashbaord" style={{background:'white',padding:0}}>
        <div className="width-settimg addtionwidth-setting sap-page">
          <div className="sap-title">Wash Count Templates</div>
          <div className="sap-sub">
            You define which monthly wash counts exist on the platform. Providers activate the ones they want and set
            their own prices — they cannot invent new counts. This keeps "the mid tier" the same product across every
            provider, which is what makes renewal-rate-by-tier a usable metric.
          </div>

          <div className="sap-info">
            <span>💡</span>
            <span>
              Each template locks <strong>wash count to a weekly frequency</strong>. A customer buying the 8-wash tier
              can select exactly 2 preferred days. This prevents buying the cheapest tier and selecting 4 days a week.
            </span>
          </div>

          <div className="sap-sh">
            <div className="sap-sh-title">Available templates</div>
            <button className="sap-btn primary" onClick={() => setShowAddModal(true)}>+ Add template</button>
          </div>

          {loading ? (
            <div className="sap-muted">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="sap-muted">No templates yet. Click "+ Add template" to create one.</div>
          ) : (
            <div className="sap-tpl-grid">
              {templates.map((tpl) => {
                const valid = isValidWeeklyPattern(tpl.washCount, tpl.daysPerWeek);
                const usedBy = usageCount[tpl._id] ?? 0;
                return (
                  <div className={`sap-tpl-card ${!valid ? 'blocked' : ''}`} key={tpl._id}>
                    <div className="sap-tpl-head">
                      <div className="sap-tpl-count" style={!valid ? { color: '#991b1b' } : {}}>{tpl.washCount}</div>
                      <button
                        type="button"
                        className={`sap-toggle ${tpl.isActive && valid ? 'on' : ''}`}
                        onClick={() => valid && handleToggle(tpl)}
                        title={!valid ? 'Cannot activate — invalid pattern' : tpl.isActive ? 'Deactivate' : 'Activate'}
                        style={!valid ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                      >
                        <span className="sap-toggle-knob" />
                      </button>
                    </div>
                    <div className="sap-tpl-body">
                      <div className="sap-tpl-row"><span>Frequency</span><b>{valid ? `${tpl.daysPerWeek}× / week` : <span style={{color:'#991b1b'}}>—</span>}</b></div>
                      <div className="sap-tpl-row"><span>Days selectable</span><b>{valid ? tpl.daysPerWeek : <span style={{color:'#991b1b'}}>—</span>}</b></div>
                      <div className="sap-tpl-row"><span>Default name</span><b>{tpl.defaultDisplayName}</b></div>
                      <div className="sap-tpl-row"><span>Used by</span><b>{usedBy} sets</b></div>
                      {!valid && (
                        <div className="sap-tpl-note">
                          ⚠ No weekly pattern produces {tpl.washCount} washes in a calendar month. 2 days/week = 8–9, 3 days/week = 12–13. Cannot be activated.
                        </div>
                      )}
                      <button
                        type="button"
                        className="sap-btn danger sm"
                        style={{ marginTop: 4 ,display: 'flex',justifyContent: 'center',gap: '3px',padding: '10px',fontSize: "12px",alignItems: 'center',fontFamily: 'Manrope'}}
                        onClick={() => handleDelete(tpl)}
                      >
                        <MdDeleteOutline size={12} style={{ verticalAlign: 'middle' }} /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {showAddModal && (
            <div className="sap-modal-overlay" onClick={() => setShowAddModal(false)}>
              <div className="sap-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sap-modal-head">
                  <h3>Add Wash Count Template</h3>
                  <button className="sap-modal-close" onClick={() => setShowAddModal(false)}>×</button>
                </div>
                <form onSubmit={handleAdd}>
                  <div className="sap-form-row">
                    <div className="sap-form-field">
                      <label className="sap-form-label">Wash count</label>
                      <input className="sap-form-input" placeholder="e.g. 8" type="number" min="1"
                        value={form.washCount} onChange={(e) => setForm((f) => ({ ...f, washCount: e.target.value }))} />
                    </div>
                    <div className="sap-form-field">
                      <label className="sap-form-label">Days / week</label>
                      <input className="sap-form-input" placeholder="e.g. 2" type="number" min="1" max="7"
                        value={form.daysPerWeek} onChange={(e) => setForm((f) => ({ ...f, daysPerWeek: e.target.value }))} />
                    </div>
                  </div>
                  <div className="sap-form-row">
                    <div className="sap-form-field">
                      <label className="sap-form-label">Default display name</label>
                      <input className="sap-form-input" placeholder="e.g. Twice weekly"
                        value={form.defaultDisplayName} onChange={(e) => setForm((f) => ({ ...f, defaultDisplayName: e.target.value }))} />
                    </div>
                    <div className="sap-form-field">
                      <label className="sap-form-label">Sort order</label>
                      <input className="sap-form-input" placeholder="e.g. 2" type="number"
                        value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} />
                    </div>
                  </div>
                  {form.washCount && form.daysPerWeek && !isValidWeeklyPattern(Number(form.washCount), Number(form.daysPerWeek)) && (
                    <div className="sap-warn" style={{ marginBottom: 12 }}>
                      <span>⚠</span>
                      <span>No weekly pattern produces {form.washCount} washes in a calendar month. This template cannot be activated.</span>
                    </div>
                  )}
                  <div className="sap-modal-actions">
                    <button type="button" className="sap-btn ghost" onClick={() => setShowAddModal(false)}>Cancel</button>
                    <button type="submit" className="sap-btn primary" disabled={submitting}>
                      {submitting ? 'Saving…' : '+ Add template'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="sap-divider" />
        </div>
      </div>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default WashCounts;
